import { getCurrentApiEnvironment } from '../store/environment.store'

const API_HOSTS = {
    live: 'https://member-calls2.unicity.com',
    dev: 'https://member-calls2-dev.unicity.com'
} as const

const HYDRA_HOSTS = {
    live: 'https://hydra.unicity.net',
    dev: 'https://hydraqa.unicity.net'
} as const

export const getMemberCalls2Url = (path: string): string => {
    const env = getCurrentApiEnvironment()
    return `${API_HOSTS[env]}/${path.replace(/^\//, '')}`
}

export const getHydraUrl = (path: string): string => {
    const env = getCurrentApiEnvironment()
    const cleanPath = path.replace(/^\//, '')
    // On dev (hydraqa) the v5a APIs live under /v5a-test/* instead of /v5a/*.
    const versionedPath = env === 'dev' ? cleanPath.replace(/^v5a(\/|$)/, 'v5a-test$1') : cleanPath
    return `${HYDRA_HOSTS[env]}/${versionedPath}`
}

export const getFeelGreatCountriesUrl = (): string => {
    return getMemberCalls2Url('settings/countryFeelGreatV2/enable')
}

export type FetchSettingsParams = {
    countryAlpha3: string
    baStatus?: string
    ordercalC?: boolean
    baId?: string
}

export const ORDERCALC_COUNTRIES = ['MAR', 'TUR', 'ZAF', 'SAU'] as const

export const getFeelGreatApiUrl = (params: FetchSettingsParams | string, baStatus?: string): string => {
    const normalized: FetchSettingsParams = typeof params === 'string' ? { countryAlpha3: params, baStatus } : params

    const path = `products-v2/feelGreat/v3/publish/${normalized.countryAlpha3}`
    const qs = new URLSearchParams()
    if (normalized.baStatus) qs.set('baStatus', normalized.baStatus)
    if (normalized.ordercalC) qs.set('ordercalC', '1')
    if (normalized.baId) qs.set('baId', normalized.baId)
    if (getCurrentApiEnvironment() === 'dev') qs.set('isQa', '1')
    const query = qs.toString()
    return getMemberCalls2Url(query ? `${path}?${query}` : path)
}

export const getFormatApiUrl = (countryAlpha3: string, uuid: string): string => {
    return getMemberCalls2Url(`hot/format/${countryAlpha3}/feelGreat/detail-v2/${uuid}`)
}

export const getDictionaryUrl = (countryCode: string, page = 'feelGreat'): string => {
    return getMemberCalls2Url(`hot/dictionary/v2/publish/${countryCode}/${page}`)
}

export const getAddressDataUrl = (countryAlpha3: string): string => {
    return getMemberCalls2Url(`fn-misc/city/${countryAlpha3}`)
}

export const getMenuUrl = (countryAlpha3: string, baId: string): string => {
    return getMemberCalls2Url(`etlV2/menu?ushopCountryCode=${countryAlpha3.toLowerCase()}&baId=${baId}`)
}

/** POST/PUT cart UUID session: hot/formatV2/{THA}/feelGreat/address */
export const getCreateUuidUrl = (countryAlpha3: string, isOrderCalc?: boolean): string => {
    return getMemberCalls2Url(`hot/formatV2/${countryAlpha3}/feelGreat/address${isOrderCalc ? '?ordercalC=1' : ''}`)
}

/** GET format detail with optional price calculation: hot/format/{THA}/feelGreat/detail-v2/{uuid} */
export const getFormatDetailUrl = (countryAlpha3: string, uuid: string, orderCalc = false): string => {
    const query = orderCalc ? '?ordercalC=1&expandGenBeta=1' : ''
    return getMemberCalls2Url(`hot/format/${countryAlpha3}/feelGreat/detail-v2/${uuid}${query}`)
}

/** GET entry period for checkout session: etlV2/entryPeriod?ushopCountryCode={TH} */
export const getEntryPeriodUrl = (countryCode2: string): string => {
    return getMemberCalls2Url(`etlV2/entryPeriod?ushopCountryCode=${countryCode2}`)
}

/** GET validate referral: products-v2/feelGreat/v3/checkAllowReferrer?countryCode={THA}&baId={id} */
export const getCheckAllowReferrerUrl = (countryAlpha3: string, baId: string): string => {
    return getMemberCalls2Url(`products-v2/feelGreat/v3/checkAllowReferrer?countryCode=${countryAlpha3}&baId=${baId}`)
}

/** POST create account + shipping: hot/formatV2/{THA}/feelGreat/createAccount */
export const getCreateAccountUrl = (countryAlpha3: string): string => {
    const isDev = getCurrentApiEnvironment() === 'dev'
    const devQs = isDev ? '&dev=1' : ''
    return getMemberCalls2Url(`hot/formatV2/${countryAlpha3}/feelGreat/createAccount?emailDelay=1h&isSkipCheckPassword=1${devQs}`)
}

/** GET authenticated user's referral codes: hydra v5a/customers/me/referralCodes */
export const getReferralCodesUrl = (): string => {
    return getHydraUrl('v5a/customers/me/referralCodes')
}

/** GET order history list: etlV2/ordersHistory?baId=&ushopCountryCode= */
export const getOrderHistoryUrl = (baId: string, country3: string): string => {
    const env = getCurrentApiEnvironment()
    const isQa = env === 'dev' ? '&isQa=1' : ''
    return getMemberCalls2Url(`etlV2/ordersHistory?baId=${baId}&ushopCountryCode=${country3}${isQa}`)
}

/** GET order detail: etlV2/orderDetail?orderRef=&ushopCountryCode= */
export const getOrderDetailUrl = (orderRef: string, country3: string): string => {
    const env = getCurrentApiEnvironment()
    const isQa = env === 'dev' ? '&isQa=1' : ''
    return getMemberCalls2Url(`etlV2/orderDetail?orderRef=${orderRef}&ushopCountryCode=${country3}${isQa}`)
}

/** GET THA tracking: transportation/virgin/tracking/{orderNumber} */
export const getTrackingUrl = (orderNumber: string): string => getMemberCalls2Url(`transportation/virgin/tracking/${orderNumber}`)

/** GET subscription list: etlV2/subscription?ushopCountryCode=&collapse=hydra&filterOrder=feelgreat&loginId= */
export const getSubscriptionsUrl = (baId: string, country3: string): string => {
    const env = getCurrentApiEnvironment()
    const isQa = env === 'dev' ? '&isQa=1' : ''
    return getMemberCalls2Url(`etlV2/subscription?ushopCountryCode=${country3}&collapse=hydra&filterOrder=feelgreat&loginId=${baId}${isQa}`)
}

/** POST cancel subscription: proxy/autoship-v2/delete */
export const getCancelSubscriptionUrl = (): string => getMemberCalls2Url('proxy/autoship-v2/delete')

/** POST skip delivery: proxy/autoship/skip */
export const getSkipDeliveryUrl = (): string => getMemberCalls2Url('proxy/autoship/skip')

/** POST edit subscription: proxy/{country3}/subscriptions/edit */
export const getEditSubscriptionUrl = (country3: string): string => getMemberCalls2Url(`proxy/${country3}/subscriptions/edit`)

/** POST send now subscription: proxy/autoship/order */
export const getSendNowSubscriptionUrl = (): string => getMemberCalls2Url('proxy/autoship/order')

/** POST validate credit card before payment/subscription update */
export const getCreditCardCheckValidateUrl = (): string => getMemberCalls2Url('validate/creditCard/checkValidate')

/** POST format address: hot/format/{country3}/feelGreat/address-format */
export const getAddressFormatUrl = (country3: string): string => getMemberCalls2Url(`hot/format/${country3}/feelGreat/address-format`)

/** GET footer config settings: footer/data/public/{country3} */
export const getFooterConfigUrl = (country3: string): string => getMemberCalls2Url(`footer/data/public/${country3}`)

/** POST update customer (email/mobile): proxy/customers/me via MC2 */
export const getUpdateCustomerUrl = (): string => getMemberCalls2Url('proxy/customers/me')

/** POST change password: hydra v5a/customers/me/password */
export const getChangePasswordUrl = (): string => getHydraUrl('v5a/customers/me/password')

/** GET user profile from etlV2/onself */
export const getEtlOnselfUrl = (): string => getMemberCalls2Url('etlV2/onself')

/** GET validate phone number (THA): validate/phone-with-country-code */
export const getValidatePhoneUrl = (country2: string, phone: string): string =>
    getMemberCalls2Url(`validate/phone-with-country-code?country=${country2}&phone=${phone}&isValidateOnlyMobile=1`)

/** GET PaymentsV3 status by gateway_ref_id: hot/payments/v3/status/{gatewayRefId} */
export const getPaymentStatusV3Url = (gatewayRefId: string): string => getMemberCalls2Url(`hot/payments/v3/status/${gatewayRefId}`)

/** POST payment-router proxy detail (gateway_ref_id lookup — used by TW/COD,
 *  which does not go through PaymentsV3): hot/payment_router/detail/proxy */
export const getPaymentRouterProxyDetailUrl = (): string => getMemberCalls2Url('hot/payment_router/detail/proxy')

/** POST PaymentsV3 create: hot/payments/v3/{countryAlpha3}/feelGreat/variable */
export const getCreatePaymentV3Url = (countryAlpha3: string): string => getMemberCalls2Url(`hot/payments/v3/${countryAlpha3}/feelGreat/variable`)
